import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { auditService } from '../../services/auditService';
import { useAuth } from '../../context/AuthContext';
import { canAccessUsersModule } from '../../utils/permissions';
import ComparisonPanel from './ComparisonPanel';
import LocationsPanel from './LocationsPanel';
import DiscrepancyPanel from './DiscrepancyPanel';
import RecountPanel from './RecountPanel';
import RecountForm from './RecountForm';
import ValidEanPanel from './ValidEanPanel';
import AdjustmentsPanel from './AdjustmentsPanel';
import RecoPanel from './RecoPanel';
import SignoffPanel, { DECISION, SignoffDialog } from './SignoffPanel';
import { useLiveEvents } from '../../utils/liveEvents';
import {
  PAGE_SIZE_OPTIONS,
  formatScanTime,
} from '../../utils/delimitedData';
import './AuditPortal.css';
import './ComparisonPanel.css';
import './LivePanels.css';
import { ScheduleNumber } from '../../components/ScheduleStatus';
import DataGrid, { dash, numberColumn } from '../../components/DataGrid';
import { PageLoader } from '../../components/Loader';
import { KpiCards } from '../../components/ScheduleKpis';
import ManageTeamModal from '../../components/Manpower/ManageTeamModal';

const PORTAL_TABS = ['scans', 'validean', 'locations', 'discrepancies', 'recounts', 'adjustments', 'compare', 'reco', 'products', 'signoff'];
const TAB_LABELS = {
  scans: 'Live scans',
  validean: 'Valid EAN',
  locations: 'Zones & locations',
  discrepancies: 'Discrepancies',
  recounts: 'Recounts',
  adjustments: 'Manual adjustments',
  compare: 'Comparison',
  reco: 'Reco',
  products: 'Master products',
  signoff: 'Sign-off',
};
// Updates normally arrive over the live stream; this timer is the fallback.
const POLL_LIVE_MS = 60000;
const POLL_OFFLINE_MS = 15000;

const fmt = (value) => Number(value || 0).toLocaleString();

// Master products tab (server pages through the list).
const PRODUCT_COLUMNS = [
  { headerName: 'EAN', field: 'ean', minWidth: 160 },
  { headerName: 'Product name', field: 'product_name', minWidth: 240, flex: 2 },
  { headerName: 'Store code', field: 'store_code', valueFormatter: (p) => dash(p.value) },
  { headerName: 'Internal code', field: 'internal_code', valueFormatter: (p) => dash(p.value) },
];

function AuditPortal() {
  const { auditId } = useParams();
  const { user } = useAuth();
  // Managers can upload book stock; client users only view.
  const canManage = canAccessUsersModule(user);
  const backLink = canManage
    ? { to: '/audits', label: 'Back to schedules' }
    : { to: '/client', label: 'Back to your schedules' };
  const [audit, setAudit] = useState(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('scans');
  const [overview, setOverview] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [recountPrefill, setRecountPrefill] = useState(null);
  const [signing, setSigning] = useState(false);
  const [locationCodes, setLocationCodes] = useState([]);
  const isClient = user?.role_name === 'client';
  const [loadingAudit, setLoadingAudit] = useState(true);
  const [error, setError] = useState('');

  const [masterMeta, setMasterMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [productTotal, setProductTotal] = useState(0);
  const [productPage, setProductPage] = useState(0);
  const [productPageSize, setProductPageSize] = useState(20);
  const [productSearchInput, setProductSearchInput] = useState('');
  const [productSearch, setProductSearch] = useState('');

  const [scans, setScans] = useState([]);
  const [scanTotal, setScanTotal] = useState(0);
  const [scanPage, setScanPage] = useState(0);
  const [scanPageSize, setScanPageSize] = useState(20);
  const [scanSearchInput, setScanSearchInput] = useState('');
  const [scanSearch, setScanSearch] = useState('');
  const [scanStatus, setScanStatus] = useState('');
  const [scanLocationInput, setScanLocationInput] = useState('');
  const [scanLocation, setScanLocation] = useState('');

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingScans, setLoadingScans] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  useEffect(() => {
    setLoadingAudit(true);
    setError('');

    Promise.all([
      auditService.getById(auditId),
      auditService.getMasterFile(auditId).catch(() => null),
      auditService.getOverview(auditId).catch(() => null),
    ])
      .then(([auditData, meta, overviewData]) => {
        setAudit(auditData);
        setMasterMeta(meta);
        setOverview(overviewData);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingAudit(false));
  }, [auditId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProductSearch(productSearchInput.trim());
      setProductPage(0);
    }, 350);

    return () => clearTimeout(timer);
  }, [productSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setScanSearch(scanSearchInput.trim());
      setScanPage(0);
    }, 350);

    return () => clearTimeout(timer);
  }, [scanSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setScanLocation(scanLocationInput.trim());
      setScanPage(0);
    }, 350);

    return () => clearTimeout(timer);
  }, [scanLocationInput]);

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await auditService.getOverview(auditId));
    } catch (err) {
      // The cards keep their last values.
    }
  }, [auditId]);

  // Other tabs: the summary cards come from the overview; just mark the refresh time.
  const loadScanSummary = useCallback(() => setLastSyncedAt(new Date()), []);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);

    try {
      const [meta, productData] = await Promise.all([
        auditService.getMasterFile(auditId),
        auditService.getProducts(auditId, {
          search: productSearch || undefined,
          limit: productPageSize,
          offset: productPage * productPageSize,
        }),
      ]);

      setMasterMeta(meta);
      setProducts(productData.items || []);
      setProductTotal(productData.total || 0);
      setLastSyncedAt(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingProducts(false);
    }
  }, [auditId, productPage, productPageSize, productSearch]);

  const loadScans = useCallback(async () => {
    setLoadingScans(true);

    try {
      const scanData = await auditService.getScans(auditId, {
        search: scanSearch || undefined,
        status: scanStatus || undefined,
        location: scanLocation || undefined,
        limit: scanPageSize,
        offset: scanPage * scanPageSize,
      });

      setScans(scanData.items || []);
      setScanTotal(scanData.total || 0);
      setLastSyncedAt(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingScans(false);
    }
  }, [auditId, scanPage, scanPageSize, scanSearch, scanStatus, scanLocation]);

  useEffect(() => {
    if (activeTab === 'products') {
      loadProducts();
    }
  }, [activeTab, loadProducts]);

  useEffect(() => {
    if (activeTab === 'scans') {
      loadScans();
    }
  }, [activeTab, loadScans]);

  // Something changed on the server (scans synced, recount, sign-off…):
  // refresh the cards and whatever tab is open.
  const refreshAll = useCallback(() => {
    loadOverview();
    if (activeTab === 'scans') {
      loadScans();
    } else {
      loadScanSummary();
    }
    // Panels reload when refreshKey changes.
    setRefreshKey((key) => key + 1);
  }, [activeTab, loadOverview, loadScanSummary, loadScans]);

  const liveStatus = useLiveEvents(
    audit ? `/audits/${auditId}/events` : null,
    refreshAll,
    // Phones upload in bursts; one refresh per few seconds is plenty and
    // keeps the server load low when many people watch the same audit.
    { debounceMs: 4000 },
  );

  useEffect(() => {
    const timer = setInterval(
      refreshAll,
      liveStatus === 'live' ? POLL_LIVE_MS : POLL_OFFLINE_MS,
    );
    return () => clearInterval(timer);
  }, [refreshAll, liveStatus]);

  const openRecountForm = useCallback((prefill) => setRecountPrefill(prefill || {}), []);

  const productTotalPages = Math.max(Math.ceil(productTotal / productPageSize), 1);
  const scanTotalPages = Math.max(Math.ceil(scanTotal / scanPageSize), 1);

  if (loadingAudit) {
    return (
      <div className="page audit-portal">
        <PageLoader label="Loading schedule portal…" />
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="page audit-portal">
        <p className="audit-portal__empty">Schedule not found.</p>
        <Link to={backLink.to} className="audit-portal__back-link">
          {backLink.label}
        </Link>
      </div>
    );
  }

  // Live scans tab (server pages, filters and searches the scans).
  const scanColumns = [
    {
      headerName: 'Scanned at',
      field: 'scanned_at',
      minWidth: 180,
      valueFormatter: (p) => formatScanTime(p.value),
      comparator: (a, b) => String(a).localeCompare(String(b)),
    },
    { headerName: 'EAN', field: 'ean', minWidth: 160 },
    { headerName: 'Product name', field: 'product_name', minWidth: 200, flex: 2, valueFormatter: (p) => dash(p.value) },
    {
      headerName: 'Location',
      field: 'location',
      cellRenderer: (p) =>
        p.value ? (
          <button
            type="button"
            className="audit-portal__loc"
            onClick={() => setScanLocationInput(p.value)}
            title="Show only this location"
          >
            {p.value}
          </button>
        ) : (
          '—'
        ),
    },
    numberColumn('Qty', 'quantity', null, { colDef: { minWidth: 90 } }),
    { headerName: 'Auditor', field: 'scanned_by_name', valueFormatter: (p) => dash(p.value) },
    { headerName: 'Device', field: 'device_id', valueFormatter: (p) => dash(p.value) },
    {
      headerName: 'Status',
      colId: 'status',
      minWidth: 220,
      autoHeight: true,
      valueGetter: (p) => (p.data.original_ean ? 'Invalid resolved' : p.data.status === 'invalid' ? 'Invalid' : 'Valid'),
      cellRenderer: (p) => {
        const scan = p.data;
        return scan.original_ean ? (
          <span className="audit-portal__resolved">
            <span className="audit-portal__status audit-portal__status--resolved">Invalid resolved</span>
            <small>
              {[scan.corrected_by_name, scan.corrected_at ? formatScanTime(scan.corrected_at) : '']
                .filter(Boolean)
                .join(' · ') || 'Resolved'}{' '}
              · was {scan.original_ean}
            </small>
          </span>
        ) : (
          <span className={`audit-portal__status audit-portal__status--${scan.status}`}>
            {scan.status === 'invalid' ? 'Invalid' : 'Valid'}
          </span>
        );
      },
    },
  ];

  return (
    <div className="page audit-portal">
      {teamOpen ? (
        <ManageTeamModal
          auditId={auditId}
          auditName={audit.name}
          onClose={() => setTeamOpen(false)}
          onSaved={(saved) => {
            if (saved) setAudit((prev) => ({ ...prev, ...saved }));
            refreshAll();
          }}
        />
      ) : null}
      <header className="audit-portal__header">
        <div>
          <p className="page__eyebrow">Schedule portal</p>
          <h1>{audit.name}</h1>
          <p className="audit-portal__schedule">
            Schedule <ScheduleNumber audit={audit} />
          </p>
          <p className="audit-portal__subtitle">
            {format(parseISO(audit.start_at), 'MMM d, yyyy HH:mm')} –{' '}
            {format(parseISO(audit.end_at), 'MMM d, yyyy HH:mm')}
          </p>
        </div>

        <div className="audit-portal__header-actions">
          {lastSyncedAt && (
            <span
              className={`audit-portal__live-badge${liveStatus === 'live' ? '' : ' audit-portal__live-badge--slow'}`}
              title={
                liveStatus === 'live'
                  ? 'Connected: new scans appear within seconds of the phone uploading them.'
                  : 'Live connection unavailable: checking every 15 seconds instead.'
              }
            >
              {liveStatus === 'live' ? 'Live' : 'Auto-refresh'} · updated {lastSyncedAt.toLocaleTimeString()}
            </span>
          )}
          {canManage && !isClient ? (
            <button type="button" className="audit-portal__team-btn" onClick={() => setTeamOpen(true)}>
              Manage team
            </button>
          ) : null}
          {isClient ? (
            <button type="button" className="audit-portal__signoff-btn" onClick={() => setSigning(true)}>
              Review &amp; sign off
            </button>
          ) : null}
          <Link to={backLink.to} className="audit-portal__back-link">
            {backLink.label}
          </Link>
        </div>
      </header>

      {error && (
        <div className="audit-portal__alert" role="alert">
          {error}
        </div>
      )}

      {overview?.kpis ? (
        <div className="kpi-grid--board audit-portal__kpis">
          <KpiCards kpis={overview.kpis} progress={overview.progress} onOpen={setActiveTab} />
        </div>
      ) : null}

      <section className="audit-portal__summary audit-portal__summary--three">
        <article>
          <span>Discrepancy alerts</span>
          <strong className={overview?.discrepancies?.flagged ? 'audit-portal__warn' : ''}>
            {overview?.discrepancies?.has_book ? `${fmt(overview.discrepancies.flagged)} items` : '—'}
          </strong>
          <p>
            {overview?.discrepancies?.has_book ? (
              <button type="button" className="audit-portal__card-link" onClick={() => setActiveTab('discrepancies')}>
                {fmt(overview.discrepancies.awaiting_recount)} awaiting recount · {fmt(overview.discrepancies.recounted)} recounted
              </button>
            ) : (
              'Needs book stock (Comparison tab)'
            )}
          </p>
        </article>
        <article>
          <span>Recount requests</span>
          <strong>
            {fmt((overview?.recounts?.open || 0) + (overview?.recounts?.acknowledged || 0))} open
          </strong>
          <p>
            <button type="button" className="audit-portal__card-link" onClick={() => setActiveTab('recounts')}>
              {fmt(overview?.recounts?.acknowledged)} in progress · {fmt(overview?.recounts?.done)} done
            </button>
          </p>
        </article>
        <article>
          <span>Client sign-off</span>
          <strong>
            {overview?.signoffs?.length
              ? DECISION[overview.signoffs[0].decision]?.label || overview.signoffs[0].decision
              : 'Pending'}
          </strong>
          <p>
            {overview?.signoffs?.length ? (
              <button type="button" className="audit-portal__card-link" onClick={() => setActiveTab('signoff')}>
                {overview.signoffs[0].signer_name}
                {overview.signoffs.length > 1 ? ` +${overview.signoffs.length - 1} more` : ''}
              </button>
            ) : (
              `Master file: ${masterMeta?.row_count != null ? fmt(masterMeta.row_count) : 0} products`
            )}
          </p>
        </article>
      </section>

      <section className="audit-portal__tabs">
        {PORTAL_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            className={activeTab === tab ? 'audit-portal__tab--active' : ''}
            onClick={() => setActiveTab(tab)}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </section>

      {recountPrefill ? (
        <RecountForm
          auditId={auditId}
          prefill={recountPrefill}
          locations={locationCodes}
          onClose={() => setRecountPrefill(null)}
          onSent={() => {
            refreshAll();
            setActiveTab((tab) => (tab === 'scans' || tab === 'products' ? 'recounts' : tab));
          }}
        />
      ) : null}

      {signing ? (
        <SignoffDialog
          auditId={auditId}
          audit={audit}
          overview={overview}
          onClose={() => setSigning(false)}
          onSigned={() => {
            refreshAll();
            setActiveTab('signoff');
          }}
        />
      ) : null}

      {activeTab === 'validean' ? (
        <ValidEanPanel auditId={auditId} canEdit={isClient} refreshKey={refreshKey} />
      ) : activeTab === 'locations' ? (
        <LocationsPanel
          auditId={auditId}
          auditName={audit.name}
          canManage={canManage}
          refreshKey={refreshKey}
          onRequestRecount={openRecountForm}
          onLocationsLoaded={setLocationCodes}
        />
      ) : activeTab === 'discrepancies' ? (
        <DiscrepancyPanel
          auditId={auditId}
          auditName={audit.name}
          canManage={canManage}
          refreshKey={refreshKey}
          onRequestRecount={openRecountForm}
        />
      ) : activeTab === 'recounts' ? (
        <RecountPanel
          auditId={auditId}
          canManage={canManage}
          refreshKey={refreshKey}
          onNewRequest={openRecountForm}
        />
      ) : activeTab === 'adjustments' ? (
        <AdjustmentsPanel auditId={auditId} canEdit={canManage} refreshKey={refreshKey} />
      ) : activeTab === 'signoff' ? (
        <SignoffPanel
          auditId={auditId}
          isClient={isClient}
          refreshKey={refreshKey}
          onSignClick={() => setSigning(true)}
        />
      ) : activeTab === 'compare' ? (
        <ComparisonPanel auditId={auditId} auditName={audit.name} canManage={canManage} />
      ) : activeTab === 'reco' ? (
        <RecoPanel auditId={auditId} canEdit={canManage} refreshKey={refreshKey} />
      ) : activeTab === 'products' ? (
        <section className="audit-portal__panel">
          <header className="audit-portal__toolbar">
            <label>
              Search EAN or product
              <input
                type="search"
                value={productSearchInput}
                onChange={(event) => setProductSearchInput(event.target.value)}
                placeholder="Search synced products..."
              />
            </label>

            <label>
              Rows per page
              <select
                value={productPageSize}
                onChange={(event) => {
                  setProductPageSize(Number(event.target.value));
                  setProductPage(0);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>

            <button type="button" onClick={loadProducts}>
              Refresh now
            </button>
          </header>

          <div className="audit-portal__table-shell">
            {loadingProducts && products.length === 0 ? (
              <PageLoader label="Loading synced products…" />
            ) : products.length === 0 ? (
              <p className="audit-portal__empty">
                No synced products yet. Upload a master file from the schedule edit form.
              </p>
            ) : (
              <DataGrid
                rowData={products}
                columnDefs={PRODUCT_COLUMNS}
                getRowId={null}
                search={false}
                pageSize={0}
              />
            )}
          </div>

          <footer className="audit-portal__pagination">
            <span>
              Showing {productTotal === 0 ? 0 : productPage * productPageSize + 1}–
              {Math.min((productPage + 1) * productPageSize, productTotal)} of{' '}
              {productTotal}
            </span>
            <div>
              <button
                type="button"
                disabled={productPage === 0 || loadingProducts}
                onClick={() => setProductPage((prev) => prev - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={productPage + 1 >= productTotalPages || loadingProducts}
                onClick={() => setProductPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          </footer>
        </section>
      ) : (
        <section className="audit-portal__panel">
          <header className="audit-portal__toolbar">
            <label>
              Search scans
              <input
                type="search"
                value={scanSearchInput}
                onChange={(event) => setScanSearchInput(event.target.value)}
                placeholder="Search EAN, product, auditor or location..."
              />
            </label>

            <label>
              Location
              <input
                type="search"
                value={scanLocationInput}
                onChange={(event) => setScanLocationInput(event.target.value)}
                placeholder="e.g. LOC-RACK-A12"
                list="portal-location-options"
              />
              <datalist id="portal-location-options">
                {locationCodes.slice(0, 2000).map((code) => (
                  <option key={code} value={code} />
                ))}
              </datalist>
            </label>

            <label>
              Status
              <select
                value={scanStatus}
                onChange={(event) => {
                  setScanStatus(event.target.value);
                  setScanPage(0);
                }}
              >
                <option value="">All scans</option>
                <option value="valid">Valid only</option>
                <option value="invalid">Invalid only</option>
                <option value="resolved">Invalid resolved</option>
              </select>
            </label>

            <label>
              Rows per page
              <select
                value={scanPageSize}
                onChange={(event) => {
                  setScanPageSize(Number(event.target.value));
                  setScanPage(0);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>

            <button type="button" onClick={loadScans}>
              Refresh now
            </button>
          </header>

          <div className="audit-portal__table-shell">
            {loadingScans && scans.length === 0 ? (
              <PageLoader label="Loading live scans…" />
            ) : scans.length === 0 ? (
              <p className="audit-portal__empty">
                No scans recorded yet. Mobile scanner submissions will appear here live.
              </p>
            ) : (
              <DataGrid
                rowData={scans}
                columnDefs={scanColumns}
                getRowId={null}
                search={false}
                pageSize={0}
              />
            )}
          </div>

          <footer className="audit-portal__pagination">
            <span>
              Showing {scanTotal === 0 ? 0 : scanPage * scanPageSize + 1}–
              {Math.min((scanPage + 1) * scanPageSize, scanTotal)} of {scanTotal}
            </span>
            <div>
              <button
                type="button"
                disabled={scanPage === 0 || loadingScans}
                onClick={() => setScanPage((prev) => prev - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={scanPage + 1 >= scanTotalPages || loadingScans}
                onClick={() => setScanPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          </footer>
        </section>
      )}
    </div>
  );
}

export default AuditPortal;
